import React, { useLayoutEffect, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import Loader from 'components/D3Loader'
import { modalActions } from 'store/modal'
import { authActions } from 'store/user'
import { replaceTo } from 'navigation'
import {
  clearConsentStatus,
  completeConsentQuestion,
  skipConsentQuestion,
  downloadConsentPDF,
  downloadSignConsentPDF,
  endConsent,
  onFetchConsent,
  onConsentOpen,
  onConsentReject,
  onSetSubmissionConsent,
  submitConsent,
  uploadConsentFile
} from '../modules/Consent'
import ConsentPage from '../components/ConsentPage'

const mapStateToProps = state => {
  const { consent, user, loading, strings } = state
  const readOnlyConsent =
    user?.site?.config?.consent_2?.paper_only ||
    user?.site?.config?.consent_2?.read_only

  return {
    ...consent,
    strings,
    firstName: user.fname,
    fullName: `${user.fname} ${user.lname}`,
    hasConsented: consent?.consentStatus?.hasConsented,
    userHasConsented: user.has_consented, // Also check user reducer
    loading: loading.general,
    studyID: user.study_id,
    pendingConsents: consent.v2Consents,
    humiIsabellaDisabled:
      user?.study?.config?.is_rewardless_study &&
      user?.study?.config?.is_avatarless_study,
    readOnlyConsent
  }
}

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  // Ensure thunks and actions are dispatched so side-effects run
  clearConsentStatus: () => dispatch(clearConsentStatus()),
  closeModal: () => dispatch(modalActions.closeModal()),
  completeConsentQuestion: (id, data) => dispatch(completeConsentQuestion(id, data)),
  skipConsentQuestion: id => dispatch(skipConsentQuestion(id)),
  downloadConsentPDF: (name, version, id) => dispatch(downloadConsentPDF(name, version, id)),
  downloadSignConsentPDF: (studyId, name, id) => dispatch(downloadSignConsentPDF(studyId, name, id)),
  endConsent: () => dispatch(endConsent()),
  onConsentOpen: (studyID, consentId, version) => dispatch(onConsentOpen(studyID, consentId, version)),
  onConsentReject: (studyID, consentId, version) => dispatch(onConsentReject(studyID, consentId, version)),
  onFetchConsent: studyID => dispatch(onFetchConsent(studyID)),
  onLogout: () => dispatch(authActions.logout()),
  onSetSubmissionConsent: formatted => dispatch(onSetSubmissionConsent(formatted)),
  onFetchConsent: studyID => dispatch(onFetchConsent(studyID)),
  openModal: modalProps => dispatch(modalActions.openModal(modalProps)),
  submitConsent: (consent, studyID, errorMessage) => dispatch(submitConsent(consent, studyID, errorMessage)),
  uploadConsentFile: formData => dispatch(uploadConsentFile(formData))
})

const ConsentContainerComponent = props => {
  const {
    loading,
    hasConsented,
    userHasConsented,
    v2Consents,
    consentForm,
    studyID,
    onFetchConsent
  } = props
  const pathname = props.location?.pathname ?? ''

  // GOLD-11140: When on /consent with stale "no consents" state (e.g. new consent deployed while logged in),
  // trigger a fresh fetch and show Loader instead of redirecting. Only redirect after we've had a chance to fetch.
  const consentRouteFetchDispatchedRef = useRef(false)
  const hasFetchedForConsentRouteRef = useRef(false)

  // Check if user has consented (from either consent state or user state)
  const hasConsentedFlag = hasConsented || userHasConsented
  const hasNoConsents = (!v2Consents || v2Consents.length === 0) && (!consentForm || Object.keys(consentForm).length === 0)
  const hasMultipleConsentsNoForm = (v2Consents?.length > 1) && (!consentForm || Object.keys(consentForm).length === 0)
  const isOnConsentRoute = pathname === '/consent'

  // On /consent with no consents: trigger fetch once so we get latest data (e.g. newly deployed consent)
  useLayoutEffect(() => {
    if (isOnConsentRoute && hasConsentedFlag && hasNoConsents && !consentRouteFetchDispatchedRef.current && !loading) {
      consentRouteFetchDispatchedRef.current = true
      onFetchConsent(studyID)
    }
  }, [isOnConsentRoute, hasConsentedFlag, hasNoConsents, loading, studyID, onFetchConsent])

  // Mark that we've completed a fetch when on consent route (so we're allowed to redirect if still no consents)
  useEffect(() => {
    if (pathname !== '/consent') {
      consentRouteFetchDispatchedRef.current = false
      hasFetchedForConsentRouteRef.current = false
    } else if (!loading) {
      hasFetchedForConsentRouteRef.current = true
    }
  }, [pathname, loading])

  // If user has consented and there are no consents, redirect only after we've fetched on /consent (GOLD-11140)
  useLayoutEffect(() => {
    if (!loading && hasConsentedFlag && hasNoConsents && (!isOnConsentRoute || hasFetchedForConsentRouteRef.current)) {
      replaceTo('/my-activities')
    }
  }, [loading, hasConsentedFlag, hasNoConsents, isOnConsentRoute])

  // Multiple consents but no specific consent loaded (e.g. user opened /consent directly): send to profile to pick one (GOLD-11364)
  useLayoutEffect(() => {
    if (!loading && hasMultipleConsentsNoForm) {
      replaceTo('/profile')
    }
  }, [loading, hasMultipleConsentsNoForm])

  if (loading) return <Loader />

  // On /consent with no consents: show Loader until fetch settles (avoids redirect on stale state)
  if (isOnConsentRoute && hasConsentedFlag && hasNoConsents) {
    return <Loader />
  }

  if (hasConsentedFlag && hasNoConsents) {
    return null
  }

  if (hasMultipleConsentsNoForm) {
    return null
  }

  return <ConsentPage {...props} />
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConsentContainerComponent)
